import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Form, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import DateRangePicker, { calcDatePreset } from '../fields/DateRangePicker';
import PaginationComponent from '../PaginationComponent';
import { pointAPI } from '../../services';

const LIMIT = 10;

const fmtPct = (n) => `${n >= 0 ? '+' : ''}${Number(n).toFixed(2)}%`;
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('vi-VN') : '-');

// Lọc "LOẠI THAY ĐỔI" gộp 2 chiều dữ liệu backend: direction (ADD/DEDUCT) và change_type
// (MANUAL_ADMIN/RESET/CONVERT_100/SYSTEM_ZERO_NEGATIVE) — xem #161721, #122342.
const FILTER_OPTIONS = [
  { value: '', label: 'Tất cả' },
  { value: 'ADD', label: 'Cộng' },
  { value: 'DEDUCT', label: 'Trừ' },
  { value: 'MANUAL_ADMIN', label: 'Admin sửa tay' },
  { value: 'RESET', label: 'Admin bấm reset' },
  { value: 'CONVERT_100', label: 'Đạt 100% — chuyển đổi' },
  { value: 'SYSTEM_ZERO_NEGATIVE', label: 'Dọn tỉ lệ âm về 0' },
];
const CHANGE_TYPE_VALUES = [
  'MANUAL_ADMIN',
  'RESET',
  'CONVERT_100',
  'SYSTEM_ZERO_NEGATIVE',
];

const CHANGE_TYPE_BADGE = {
  MANUAL_ADMIN: { text: 'Admin sửa tay', bg: 'warning' },
  RESET: { text: 'Reset', bg: 'secondary' },
  CONVERT_100: { text: 'Đạt 100%', bg: 'info' },
  SYSTEM_ZERO_NEGATIVE: { text: 'Dọn tỉ lệ âm về 0', bg: 'dark' },
};

export default function EmployeePointHistoryTab({ employeeId }) {
  const [range, setRange] = useState(() => calcDatePreset('last3months'));
  const [filterValue, setFilterValue] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const fetchHistory = useCallback(async () => {
    if (!employeeId) return;
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);
      setError(null);
      const params = { page, limit: LIMIT };
      if (range.from) params.from_date = range.from;
      if (range.to) params.to_date = range.to;
      if (filterValue) {
        if (CHANGE_TYPE_VALUES.includes(filterValue)) {
          params.change_type = filterValue;
        } else {
          params.direction = filterValue;
        }
      }
      const result = await pointAPI.getEmployeePointHistory(employeeId, params);
      if (requestIdRef.current !== requestId) return;
      setRows(result?.data || []);
      setTotal(result?.total || 0);
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      const message = err?.message || 'Không thể tải lịch sử điểm';
      toast.error(message);
      setError(message);
      setRows([]);
      setTotal(0);
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  }, [employeeId, page, range, filterValue]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleRangeChange = (from, to) => {
    setRange({ from, to });
    setPage(1);
  };

  const handleFilterChange = (e) => {
    setFilterValue(e.target.value);
    setPage(1);
  };

  return (
    <div>
      <div className="row g-3 align-items-end mb-3">
        <div className="col-md-8">
          <DateRangePicker
            fromDate={range.from}
            toDate={range.to}
            onChange={handleRangeChange}
            defaultPreset="last3months"
          />
        </div>
        <div className="col-md-4">
          <Form.Group controlId="point-history-direction">
            <Form.Label>Loại thay đổi</Form.Label>
            <Form.Select size="sm" value={filterValue} onChange={handleFilterChange}>
              {FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
      </div>

      {loading && (
        <div className="text-center py-4" role="status" aria-label="Đang tải">
          <Spinner size="sm" />
        </div>
      )}

      {!loading && error && (
        <Alert variant="danger" className="py-2">{error}</Alert>
      )}

      {!loading && !error && rows.length === 0 && (
        <p className="text-muted text-center py-4 mb-0">
          Không có lịch sử thay đổi tỉ lệ thưởng trong khoảng thời gian này.
        </p>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          <Table bordered size="sm" responsive hover>
            <thead>
              <tr>
                <th>Thời gian</th>
                <th>Loại</th>
                <th>Nguồn</th>
                <th className="text-end">Giá trị</th>
                <th>Lý do</th>
                <th className="text-end">Trước</th>
                <th className="text-end">Sau (số dư)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const changeBadge = CHANGE_TYPE_BADGE[row.change_type];
                return (
                  <tr key={row.id}>
                    <td className="small">{fmtDateTime(row.created_at)}</td>
                    <td>
                      {row.direction === 'ADD' ? (
                        <Badge bg="success">Cộng</Badge>
                      ) : (
                        <Badge bg="danger">Trừ</Badge>
                      )}
                    </td>
                    <td>
                      {changeBadge ? (
                        <Badge bg={changeBadge.bg}>{changeBadge.text}</Badge>
                      ) : (
                        <span className="text-muted small">Tự động</span>
                      )}
                    </td>
                    <td className={`text-end fw-medium ${row.direction === 'ADD' ? 'text-success' : 'text-danger'}`}>
                      {row.direction === 'ADD' ? fmtPct(row.amount) : fmtPct(-row.amount)}
                      {/* #122342: khoản trừ bị chặn ở sàn 0 — nêu rõ phần bị cắt để hậu kiểm
                          biết vì sao NV không bị trừ đủ, thay vì im lặng. */}
                      {Number(row.clamped_amount) > 0 && (
                        <div className="small text-muted fw-normal">
                          (chạm sàn 0 — cắt {Number(row.clamped_amount).toFixed(2)}%)
                        </div>
                      )}
                    </td>
                    <td className="small">{row.reason || '-'}</td>
                    <td className="text-end small text-muted">{fmtPct(row.rate_before)}</td>
                    <td className="text-end fw-medium">{fmtPct(row.rate_after)}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <PaginationComponent
            totalItems={total}
            itemsPerPage={LIMIT}
            currentPage={page}
            onPageChange={setPage}
            loading={loading}
          />
        </>
      )}
    </div>
  );
}
